import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { camelCase } from 'lodash';
import { useAcl, useI18n, useTableQuery, useTutorialApi } from '@/composables';
import { useMenuBase } from '@/composables/useMenuBase';
import { useMenuReportDialogs } from '@/composables/useMenuReportDialogs';
import { ExcludeTypeEnum } from '@/components/query/url-types';
import { DrillFilterOptionsEnum } from '@/services/models/Enums';
import helpers from '@/services/helpers';
import { $emitter } from '@/services/emitter';

export interface MenuReportRowAction {
  key: string;
  icon?: string;
  title?: string;
  disabled?: boolean;
  showCondition?: boolean;
  planLimits?: string | boolean;
  click?: (event?: unknown) => void;
}

export const useMenuReportActions = () => {
  const route = useRoute();
  const { t } = useI18n();

  const menuBase = useMenuBase();
  const tableQueryApi = useTableQuery();

  const {
    store,
    computedType,
    is,
    isReport,
    checkDrildown,
    drilldownForward,
    drilldownForwardNewTab,
    report,
    reportExclude,
    editDrilldownActionText,
    getLink,
    url,
  } = menuBase;

  const { dialogActionsEvents } = useMenuReportDialogs(menuBase);

  const {
    aclCanCreate,
    aclCanEditItem,
    aclCanViewItem,
    aclCanCreateOnSection,
    isUserOrMemberAdmin,
  } = useAcl();

  const { isMainTab, isCustomTab } = url;

  const tutorialApi = useTutorialApi(getLink);

  const selectedGroup = computed(
    () => tableQueryApi.tableMenuOverride.value || tableQueryApi.tableNextReportGroup.value || route.params.type,
  );

  const normalizeGroup = (group: string) => camelCase(group);

  const selectedGroupNormalized = computed(() => normalizeGroup(selectedGroup.value as string));

  const selectedItem = computed(() => store.getTableSelectedItem());

  const nextReport = computed(() => store.getTableSelectedItem());

  const isMultipleEdit = computed(() => {
    const selectedItems = (tableQueryApi.tableSelectedItems.value as Array<{ isDirect?: boolean }>).filter(
      ({ isDirect }) => !isDirect,
    );
    return selectedItems.length > 1;
  });

  const isBulkEdit = computed(
    () => ['campaigns', 'offers', 'landings'].includes(selectedGroup.value as string) && isMultipleEdit.value,
  );

  const isAnySelectedItemsNotElements = computed(() =>
    (tableQueryApi.tableSelectedItems.value as Array<{ grouping?: string }>).some((item) =>
      ![
        'campaigns-name',
        'offers-name',
        'landings-name',
        'flows-name',
        'traffic_sources-name',
        'affiliate_networks-name',
      ].includes(item.grouping),
    ),
  );

  const bulkEditPlanLimits = computed((): string | boolean => {
    if (!isBulkEdit.value) return false;
    switch (route.params.type) {
      case 'campaigns':
        return 'bulkEditCampaigns';
      case 'offers':
        return 'bulkEditOffers';
      case 'landings':
        return 'bulkEditLandings';
    }
    return false;
  });

  const creationPlanLimits = computed((): string | boolean => {
    switch (route.params.type) {
      case 'campaigns':
        return 'campaignsAmount';
      case 'offers':
        return 'offersAmount';
      case 'landings':
        return 'landingsAmount';
      case 'flows':
        return 'flowsAmount';
      case 'affiliate-networks':
        return 'affiliateNetworksAmount';
      case 'traffic-sources':
        return 'trafficSourcesAmount';
    }
    return false;
  });

  const createFromTemplatePlanLimits = computed((): string | boolean => {
    switch (route.params.type) {
      case 'affiliate-networks':
        return 'affiliateNetworksAmount';
      case 'traffic-sources':
        return 'trafficSourcesAmount';
    }
    return false;
  });

  const startStopButtonText = computed((): string => {
    if (selectedItem.value?.DT_RowIntegrationStatus !== 'active') return t('Start');

    switch (selectedItem.value?.DT_RowCampaignExternalStatus) {
      case '1':
      case '3':
      case '8':
      case '9':
        return t('Start');
      case '2':
      case '4':
      case '6':
      case '7':
        return t('Stop');
      default:
        return '';
    }
  });

  const startStopButtonIcon = computed(() => {
    if (startStopButtonText.value === t('Start')) return 'play';
    if (startStopButtonText.value === t('Stop')) return 'stop';
    return '';
  });

  const startStopPlanLimits = computed((): string | boolean => {
    switch (selectedItem.value?.DT_RowIntegrationType) {
      case 'propeller':
        return 'integrationPropellerads';
      case 'mgid':
        return 'integrationMgid';
      case 'revcontent':
        return 'integrationRevcontent';
      case 'exoclick':
        return 'integrationExoclick';
      case 'trafficjunky':
        return 'integrationTrafficjunky';
      case 'facebook':
        return 'integrationFacebookConversions';
    }
    return false;
  });

  const bulkReportPlanLimits = computed((): string | boolean => {
    if (tableQueryApi.tableSelectedItems.value.length <= 1) return '';
    if (isCustomTab.value) return 'bulkReportCustomParams';
    switch (route.params.type) {
      case 'campaigns':
        return 'bulkReportCampaign';
      case 'offers':
        return 'bulkReportOffer';
      case 'landings':
        return 'bulkReportLanding';
      case 'flows':
        return 'bulkReportFlow';
      case 'traffic-sources':
        return 'bulkReportTrafficSource';
      case 'affiliate-networks':
        return 'bulkReportAffiliateNetwork';
    }
    return false;
  });

  const reportsWithArchiveButton = ['campaigns', 'offers', 'landings', 'flows', 'traffic-sources', 'affiliate-networks'];

  const archiveDisallowed = computed(
    () => reportsWithArchiveButton.indexOf(selectedGroup.value as string) === -1,
  );

  const editDisallowed = computed(
    () => !!(nextReport.value.id && aclCanEditItem(nextReport.value)),
  );

  const viewDisallowed = computed(
    () => !!(nextReport.value.id && aclCanViewItem(nextReport.value)),
  );

  const bulkEditDisallowed = computed(() => {
    const selectedItems = (tableQueryApi.tableSelectedItems.value as Array<{
      isDirect?: boolean;
      DT_RowId: string;
      DT_RowWorkspaceId: string;
    }>).filter(({ isDirect }) => !isDirect);
    const isBulkEditAllowed = dialogActionsEvents.value[selectedGroupNormalized.value]?.bulkEdit;
    if (isBulkEditAllowed && selectedItems.length > 1) {
      if (selectedItems.length > 50) return false;

      return selectedItems.every(({ DT_RowId: id, DT_RowWorkspaceId: workspaceId }) =>
        selectedItems[0].DT_RowWorkspaceId === workspaceId && aclCanViewItem({ id, workspaceId }));
    }

    return viewDisallowed.value;
  });

  const createDisallowed = computed(() => !aclCanCreate.value);

  const duplicateDisallowed = computed(
    () =>
      !aclCanCreateOnSection({ workspaceId: selectedItem.value.workspaceId as string })
      || !editDisallowed.value
      || !nextReport.value.id,
  );

  const toArchive = () =>
    (tableQueryApi.tableSelectedItems.value as Array<{
      DT_RowStatus: string;
      DT_RowId: string;
      DT_RowWorkspaceId: string;
    }>).filter((item) =>
      item.DT_RowStatus !== 'archived' && aclCanEditItem({
        id: item.DT_RowId,
        workspaceId: item.DT_RowWorkspaceId,
      }),
    );

  const toRestore = () =>
    (tableQueryApi.tableSelectedItems.value as Array<{ DT_RowStatus: string }>).filter(
      (item) => item.DT_RowStatus === 'archived',
    );

  const copyUrl = () => {
    const urlValue = tableQueryApi.tablePreviewLink.value;
    const el = document.createElement('textarea');
    el.value = urlValue;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  };

  const resendPostbacks = () => {
    $emitter.emit('open-dialog', {
      name: 'ResendPostbacks',
      data: {
        isBeforeSend: true,
      },
    });
  };

  const handleDrilldownForward = () => {
    if (!checkDrildown()) return;

    $emitter.emit('freeze-table');

    helpers.afterBrowserRedraw(() => {
      drilldownForward();
      $emitter.emit('reload-report');
    });
  };

  const handleDrilldownForwardNewTab = () => {
    if (!checkDrildown()) return;

    $emitter.emit('freeze-table');

    helpers.afterBrowserRedraw(() => {
      drilldownForwardNewTab();
      $emitter.emit('reload-report');
    });
  };

  const handleExclude = () => {
    if (!checkDrildown()) return;

    $emitter.emit('freeze-table');

    helpers.afterBrowserRedraw(() => {
      drilldownForward(null, {
        tableNextReportExclude: true,
        tableNextReportExcludeType: ExcludeTypeEnum.concat,
      });
      $emitter.emit('reload-report');
    });
  };

  const handleExcludeWithNewTab = () => {
    if (!checkDrildown()) return;

    $emitter.emit('freeze-table');

    helpers.afterBrowserRedraw(() => {
      drilldownForwardNewTab(null, {
        tableNextReportExclude: true,
        tableNextReportExcludeType: ExcludeTypeEnum.concat,
      });
      $emitter.emit('reload-report');
    });
  };

  const handleExcludeStartNewTab = () => {
    reportExclude();
  };

  const handleChangeWorkspace = () => {
    $emitter.emit('open-dialog', { name: 'ChangeWorkspace' });
  };

  const editDrilldownAction = computed((): MenuReportRowAction => {
    const drilldownItems = tableQueryApi.tableDrilldown.value;
    const lastDrill: Record<string, any> = drilldownItems?.[drilldownItems.length - 1] || {};
    const normalizedGroup = normalizeGroup(lastDrill.filterTables);
    const drillCanBeEdited = lastDrill
      && lastDrill.filterOptions === DrillFilterOptionsEnum.equals
      && lastDrill.children.length === 0;

    return {
      key: `editDrilldown_${route.params.type}`,
      icon: 'far fa-pen',
      title: drillCanBeEdited && editDrilldownActionText(normalizedGroup),
      showCondition: drillCanBeEdited && !!dialogActionsEvents.value[normalizedGroup]?.edit,
      disabled: !aclCanViewItem({
        id: lastDrill.filterValues,
        workspaceId: lastDrill.workspaceId,
      }),
      click: () => dialogActionsEvents.value[normalizedGroup]?.edit(lastDrill.filterValues),
    };
  });

  const itemActions = computed(() => {
    const selectedItems = tableQueryApi.tableSelectedItems.value;
    const group = selectedGroupNormalized.value;
    const events = dialogActionsEvents.value;

    return {
      edit: {
        key: `edit_${route.params.type}`,
        icon: 'far fa-pen',
        title: isBulkEdit.value ? t('Bulk Edit') : t('Edit'),
        planLimits: bulkEditPlanLimits.value,
        showCondition: !!events[group]?.edit,
        disabled: !bulkEditDisallowed.value,
        click: () => {
          const action = selectedItems.length > 1 ? 'bulkEdit' : 'edit';
          return events[group][action]?.();
        },
      },
      editDrilldown: editDrilldownAction.value,
      create: {
        key: `create_${route.params.type}`,
        icon: 'far fa-plus',
        title: t('New'),
        planLimits: creationPlanLimits.value,
        showCondition: !isReport() && !!events[group]?.create,
        disabled: createDisallowed.value || (is('campaigns') && store.allPrivateWorkspaces.length === 0),
        click: () => {
          events[group].create();
          tutorialApi.modal();
        },
      },
      createCampaign: {
        key: `createCampaign_${route.params.type}`,
        icon: 'far fa-plus',
        title: t('New Campaign'),
        planLimits: 'campaignsAmount',
        showCondition: !isReport()
          && !is('campaigns')
          && !!events[group]?.create,
        disabled: createDisallowed.value || (is('campaigns') && store.allPrivateWorkspaces.length === 0),
        click: () => {
          events.campaigns.create();
          tutorialApi.modal();
        },
      },
      createFromTemplate: {
        key: `createFromTemplate_${route.params.type}`,
        icon: 'far fa-plus',
        planLimits: createFromTemplatePlanLimits.value,
        title: t('New From Template'),
        showCondition: !isReport() && (is('affiliate-networks') || is('traffic-sources')),
        disabled: createDisallowed.value,
        click: () => events.common.createFromTemplate(),
      },
      drilldownHome: {
        key: `report_${route.params.type}`,
        title: t('Report'),
        icon: 'far fa-random',
        click: report,
        showCondition: !isReport(),
        disabled: !selectedItems.length,
        planLimits: bulkReportPlanLimits.value,
      },
      drilldown: {
        key: `drilldown_${route.params.type}`,
        title: t('Drilldown'),
        icon: 'far fa-random',
        click: handleDrilldownForward,
        showCondition: isReport(),
        disabled: !selectedItems.length,
        planLimits: bulkReportPlanLimits.value,
      },
      drilldownContinueNewTab: {
        key: `drilldown_${route.params.type}`,
        title: t('Drilldown with new tab'),
        icon: 'far fa-random',
        click: handleDrilldownForwardNewTab,
        showCondition: isReport(),
        disabled: !selectedItems.length,
        planLimits: bulkReportPlanLimits.value,
      },
      drilldownOpenInNewTab: {
        key: `drilldownOpenInNewTab_${route.params.type}`,
        title: t('Report with new tab'),
        icon: 'far fa-random',
        click: report,
        disabled: !selectedItems.length,
        planLimits: bulkReportPlanLimits.value,
      },
      campaignLinks: {
        key: `campaignLinks_${route.params.type}`,
        icon: 'far fa-link',
        title: t('Campaign links'),
        showCondition: is('campaigns'),
        disabled: !viewDisallowed.value,
        click: () => events.campaigns.campaignLinks(),
      },
      startStop: {
        key: `startStop_${route.params.type}`,
        icon: `far fa-${startStopButtonIcon.value}`,
        title: startStopButtonText.value,
        showCondition: is('campaigns')
          && !!selectedItem.value?.DT_RowCampaignExternalStatus
          && selectedItem.value?.DT_RowIntegrationType === 'propeller',
        disabled: ['3', '9'].includes(selectedItem.value?.DT_RowCampaignExternalStatus as string)
          || selectedItem.value?.DT_RowIntegrationStatus !== 'active',
        planLimits: startStopPlanLimits.value,
        click: () => events.campaigns.startStop({
          id: selectedItem.value.id as string,
          buttonText: startStopButtonText.value,
        }).catch((error) => $emitter.emit(
          'toast-error',
          `${t('This action can’t be done.')}  ${error?.response?.data?.errors?.status?.[0]?.message}`,
        )),
      },
      updateCost: {
        key: `updateCost_${route.params.type}`,
        icon: 'far fa-funnel-dollar',
        title: t('Update Cost'),
        showCondition: !isReport() && is('campaigns') && isUserOrMemberAdmin.value,
        disabled: !nextReport.value.id && !editDisallowed.value,
        click: () => events.campaigns.updateCost(),
      },
      duplicate: {
        key: `duplicate_${route.params.type}`,
        icon: 'far fa-copy',
        title: t('Duplicate'),
        showCondition: isMainTab.value && !!events[group]?.duplicate,
        disabled: duplicateDisallowed.value,
        click: () => events[group].duplicate(),
      },
      archive: {
        key: `archive_${route.params.type}`,
        icon: 'far fa-archive',
        title: t('Archive'),
        showCondition: !isReport() && !archiveDisallowed.value,
        disabled: !toArchive().length || !isUserOrMemberAdmin.value,
        click: () => events.common.archive(),
      },
      restore: {
        key: `restore_${route.params.type}`,
        icon: 'far fa-undo',
        title: t('Restore'),
        showCondition: !isReport() && !!toRestore().length,
        disabled: !toRestore().length || !isUserOrMemberAdmin.value,
        click: () => events.common.restore(),
      },
      copyUrl: {
        key: `copyUrl_${route.params.type}`,
        icon: 'far fa-clone',
        title: t('Copy URL'),
        showCondition: tableQueryApi.tablePreviewLink.value && viewDisallowed.value,
        click: copyUrl,
      },
      sendPostbacks: {
        key: `resend_${route.params.type}`,
        icon: 'fas fa-arrow-turn-right',
        title: t('Resend'),
        showCondition: is('postbacks') && false,
        click: resendPostbacks,
        disabled: !selectedItems.length || selectedItems.length > 100,
      },
      preview: {
        key: `preview_${route.params.type}`,
        icon: 'far fa-external-link-square-alt',
        title: t('Preview'),
        showCondition: tableQueryApi.tablePreviewLink.value && viewDisallowed.value,
        click: () => window.open(tableQueryApi.tablePreviewLink.value, '_blank'),
      },
      excludeHome: {
        key: `exclude_${route.params.type}`,
        icon: 'far fa-page-break',
        title: t('Exclude'),
        showCondition: !isReport(),
        click: handleExcludeStartNewTab,
        disabled: !selectedItems.length,
        planLimits: bulkReportPlanLimits.value,
      },
      exclude: {
        key: `exclude_${route.params.type}`,
        icon: 'far fa-page-break',
        title: t('Exclude'),
        showCondition: isReport(),
        click: handleExclude,
        disabled: !selectedItems.length || selectedItem.value.id === 'Unknown',
        planLimits: bulkReportPlanLimits.value,
      },
      excludeContinueNewTab: {
        key: `excludeWithNewTab_${route.params.type}`,
        icon: 'far fa-page-break',
        title: t('Exclude with new tab'),
        showCondition: isReport(),
        click: handleExcludeWithNewTab,
        disabled: !selectedItems.length || selectedItem.value.id === 'Unknown',
        planLimits: bulkReportPlanLimits.value,
      },
      excludeStartNewTab: {
        key: `excludeStartNewTab_${route.params.type}`,
        icon: 'far fa-page-break',
        title: t('Exclude Report with new tab'),
        showCondition: isReport(),
        click: handleExcludeStartNewTab,
        disabled: !selectedItems.length || selectedItem.value.id === 'Unknown',
        planLimits: bulkReportPlanLimits.value,
      },
      changeWorkspace: {
        key: `changeWorkspace_${route.params.type}`,
        icon: 'fas fa-window-restore',
        title: t('Change Workspace'),
        showCondition: isMainTab.value && !!events[group]?.changeWorkspace,
        click: handleChangeWorkspace,
        disabled: selectedItems.length !== 1 || isAnySelectedItemsNotElements.value || !isUserOrMemberAdmin.value,
      },
    };
  });

  const itemMenu = computed((): MenuReportRowAction[] => [
    itemActions.value.updateCost,
    itemActions.value.duplicate,
    itemActions.value.archive,
    itemActions.value.restore,
    itemActions.value.copyUrl,
    itemActions.value.sendPostbacks,
    itemActions.value.changeWorkspace,
    itemActions.value.preview,
    itemActions.value.campaignLinks,
    itemActions.value.startStop,
  ]);

  return {
    store,
    computedType,
    isReport,
    is,
    itemActions,
    itemMenu,
    dialogActionsEvents,
  };
};
